import { ResolvedOfferConfig } from "@redotech/order-tracking-common/offer-network/offer-placement";
import { OrderTrackingOrderSource } from "@redotech/order-tracking-common/order-tracking-types";
import { TrackingPageSettings } from "@redotech/order-tracking-common/tracking-page-definition";
import type { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";
import { CustomFontFamily } from "@redotech/redo-content-builder-common/brand-kit";
import { DiscountSettings } from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { TrackingPageDiscountSource } from "@redotech/redo-discounts-common/generated-discount-code-definition";
import { FulfillmentOrder } from "@redotech/redo-fulfillment-common/fulfillment";
import { ScreenSize } from "@redotech/redo-marketing-templates-common/email-builder";
import { CropConfigV2 } from "@redotech/redo-marketing-templates-common/email-template";
import { EmailBuilderVariantImage } from "@redotech/redo-merchant-marketing-rpc/schema/product-images/get-product-images";
import { Provider } from "@redotech/redo-order-common/provider";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { RedoProductData } from "@redotech/redo-product-common/product";
import { RecommendedProductFilter } from "@redotech/redo-product-common/product-filters/product-filters-types";
import { RedoRecommendedProduct } from "@redotech/redo-product-common/redo-recommended-product-schema";
import { createContext, ReactNode } from "react";
import { OfferNetworkClient } from "../../offer-network/offer-placement";

export enum TrackingPageEnvironment {
  BUILDER = "builder",
  CUSTOMER_PORTAL = "customer_portal",
}

export enum TrackingPageAccessMode {
  AUTHENTICATED = "authenticated",
  TRACKING_CODE_ONLY = "tracking_code_only",
}

// Simplified interface to only include the properties we actually need
export interface TrackingPageReturnAppSettings {
  storeUrl: string;
  merchantName: string;
  useShopifyStorefrontForCommentSold: boolean;
  exchanges: { shopSiteDomain: string; shopSiteURL: string };
}

// Collection interface for getAllCollections/getCollection
export interface CollectionInfo {
  id: string;
  name: string;
}

// Shared product upsell methods (used in both environments)
interface SharedProductUpsellMethods {
  getRecommendedBestSellingProducts: (
    numRecommendations: number,
  ) => Promise<RedoRecommendedProduct[]>;
  getRecommendedProductsByProvider: (params: {
    provider: OrderTrackingOrderSource;
    numRecommendations: number;
    excludedVariantIds: string[];
    excludedParentProductIds: string[];
    customer: { email?: string | null };
    recommendedProductFilterId?: string;
    productIds?: string[];
    orderId?: string;
  }) => Promise<{ products: RedoRecommendedProduct[] }>;
  getAllCollections: () => Promise<CollectionInfo[]>;
  getCollection: (id: string) => Promise<CollectionInfo | null>;
  selectWindow: React.MutableRefObject<Window>;
  getProductById?: (params: {
    productId: string;
  }) => Promise<RedoProductData | undefined | null>;
}

// Builder-only product upsell methods
interface BuilderProductUpsellMethods extends SharedProductUpsellMethods {
  getProductFilterById: (
    id: string,
  ) => Promise<RecommendedProductFilter | undefined>;
}

// Customer portal-only product upsell methods
interface CustomerPortalProductUpsellMethods extends SharedProductUpsellMethods {
  logPageClick: (params: {
    trackableId: string;
    trackableType: TrackableType;
    eventType: string;
    url: string;
    image?: string;
  }) => Promise<void>;
  redirectToWebsiteWithDiscountCode: (params: {
    discountCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmContent?: string;
    utmTerm?: string;
    settings: TrackingPageReturnAppSettings;
    window: React.MutableRefObject<Window>;
    selectedProduct?: {
      url: string;
      variantId?: string;
      selectedPlanId?: string;
    };
  }) => void;
}

// Shared subscription methods available in both builder and customer portal
interface SharedSubscriptionUpsellMethods {
  getSubscriptionProducts?: (params: {
    search?: string;
    page?: number;
    pageSize?: number;
  }) => Promise<{
    products: {
      shopifyProductId?: number;
      title: string;
      description?: string | null;
      handle?: string;
      images?: {
        small?: string;
        medium?: string;
        large?: string;
        original?: string;
      }[];
      variants?: {
        externalVariantId: string;
        title: string;
        prices: { unitPrice: string };
        image?: {
          small?: string;
          medium?: string;
          large?: string;
          original?: string;
        };
      }[];
    }[];
    totalCount: number;
    hasNextPage: boolean;
  }>;
  getSubscriptionPlans?: (params: {
    productId: string;
  }) => Promise<{
    plans: {
      id: number;
      externalPlanId: string;
      planName: string;
      sellingPlanId?: string | null;
      discountAmount?: string | null;
      discountType?: string | null;
      billingPolicy: { interval: string; intervalCount: number };
      deliveryPolicy: { interval: string; intervalCount: number };
    }[];
  }>;
}

// Builder subscription methods (just for populating inspector)
interface BuilderSubscriptionUpsellMethods extends SharedSubscriptionUpsellMethods {}

// Customer portal subscription methods (includes all subscription functionality)
interface CustomerPortalSubscriptionUpsellMethods extends SharedSubscriptionUpsellMethods {
  createSubscription?: (params: {
    productId: string;
    customerEmail: string;
    variantId: string;
    quantity?: number;
    orderIntervalFrequency?: number;
    orderIntervalUnit?: "day" | "week" | "month";
    chargeIntervalFrequency?: number;
    customerFirstName?: string;
    customerLastName?: string;
    customerPhone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    province?: string;
    zip?: string;
    countryCode?: string;
    orderId?: string;
  }) => Promise<{
    subscriptionId?: string;
    status?:
      | "ACTIVE"
      | "CANCELLED"
      | "EXPIRED"
      | "active"
      | "cancelled"
      | "expired";
    nextChargeDate?: string;
    success: boolean;
  }>;
  checkCustomerSubscription?: (
    customerEmail: string,
    variantIds?: string[],
  ) => Promise<{
    customerExists: boolean;
    isSubscribedToProduct: boolean;
    hasActiveSubscriptions: boolean;
  }>;
}

// Image upload methods (shared by both builder and customer portal)
export interface ImageUploadMethods {
  getProductImages: (params: {
    searchString: string;
    pageSize: number;
    afterCursor?: string;
  }) => Promise<{
    images: EmailBuilderVariantImage[];
    nextPageCursor: string | null;
  }>;
  editImageWithAi: (params: {
    imageUrl: string;
    prompt: string;
  }) => Promise<{ imageUrl: string }>;
  cropImage: (params: {
    imageUrl: string;
    cropConfig: CropConfigV2;
  }) => Promise<{ imageUrl: string }>;
  getOriginalImageUrl: (params: {
    imageUrl: string;
  }) => Promise<{ originalImageUrl: string | null }>;
}

// Base context fields shared by both builder and customer portal
interface BaseTrackingPageContext {
  textRules?: readonly TrackingTextRule[];
  stalledDays: number | null;
  trackingPageId: string;
  pageSettings: TrackingPageSettings | undefined;
  screenSize?: ScreenSize;
  order: TrackingPageOrder | undefined;
  /** Brand-kit custom fonts, when the environment already has them loaded. */
  customFontFamilies?: CustomFontFamily[] | null;
  returnAppSettings: TrackingPageReturnAppSettings;
  fulfillmentOrders: FulfillmentOrder[];
  renderReturnSteps: () => ReactNode | null;
  subscribeUrl: string;
  hasSubscriptionIntegration?: boolean;
  generateDiscountCode: (
    discountId: string,
    source: TrackingPageDiscountSource,
    orderId?: string,
  ) => Promise<{ discountCode?: string; discountSettings?: DiscountSettings }>;
  jsMapsApiEnabled?: boolean;
  /** Use the free Maps embed iframe, not the billable JS API — for list thumbnails. */
  forceEmbedMap?: boolean;
  /** Render Custom HTML as sanitized static markup, never a live frame — for thumbnails. */
  staticPreview?: boolean;
  geocodeTrackingLocation?: (
    orderId: string,
    shipmentId: string,
  ) => Promise<{ latitude: number; longitude: number } | null>;
  geocodeMapLocation?: (location: {
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postalCode?: string | null;
  }) => Promise<{ latitude: number; longitude: number } | null>;
}

// Builder context (merchant side)
export interface BuilderTrackingPageContext extends BaseTrackingPageContext {
  environment: TrackingPageEnvironment.BUILDER;
  /** @deprecated Prefer `availableProviders` / `defaultProvider`. */
  providers: Provider[];
  /** Providers the team has connected — feeds the provider selector. */
  availableProviders: Provider[];
  /** Team's default provider, if any. */
  defaultProvider?: Provider;
  productUpsell: BuilderProductUpsellMethods;
  subscriptionUpsell?: BuilderSubscriptionUpsellMethods;
  forceRehydrateBlock?: (blockId: string) => void;
  blockRehydrationCounters?: Record<string, number>;
  imageMethods: ImageUploadMethods;
  /** Feature flag: show the third-party Offers block in the block picker. */
  offersBlockEnabled?: boolean;
  /** Offer-network placement management, backed by marketing RPCs. */
  offerNetwork?: OfferNetworkClient;
  /**
   * Store-wide loading background color (TrackingPageGlobalSettings), shared
   * across all tracking pages — not part of the per-page TrackingPageSettings.
   */
  loadingBackgroundColor?: string;
  onLoadingBackgroundColorChange?: (color: string) => void;
  hideNavigation?: boolean;
  onHideNavigationChange?: (value: boolean) => void;
  hideNavbar?: boolean;
  onHideNavbarChange?: (value: boolean) => void;
}

interface CustomerPortalTrackingPageContextBase extends BaseTrackingPageContext {
  environment: TrackingPageEnvironment.CUSTOMER_PORTAL;
  productUpsell: CustomerPortalProductUpsellMethods;
  trackingUpdates?: {
    submit: (params: {
      phoneNumber: string;
      marketingConsent: boolean;
    }) => Promise<{ success: boolean; error?: string }>;
  };
}

export interface AuthenticatedTrackingPageContext extends CustomerPortalTrackingPageContextBase {
  accessMode: TrackingPageAccessMode.AUTHENTICATED;
  subscriptionUpsell?: CustomerPortalSubscriptionUpsellMethods;
  /** Client-safe offer SDK config keyed by offerPlacementId (from getTrackingPage). */
  resolvedOffers?: Record<string, ResolvedOfferConfig>;
}

export interface TrackingCodeOnlyTrackingPageContext extends CustomerPortalTrackingPageContextBase {
  accessMode: TrackingPageAccessMode.TRACKING_CODE_ONLY;
  subscriptionUpsell?: never;
  resolvedOffers?: never;
}

export type CustomerPortalTrackingPageContext =
  | AuthenticatedTrackingPageContext
  | TrackingCodeOnlyTrackingPageContext;

// Discriminated union for type safety
export type TrackingPageContext =
  | BuilderTrackingPageContext
  | CustomerPortalTrackingPageContext;

export const TrackingPageContext = createContext<
  TrackingPageContext | undefined
>(undefined);
